import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import useIntersectionObserver from "../../../app/common/useIntersectionObserver";
import { API_URL } from "../../../app/common/constants";

import AvatarComponent from "./AvatarComponent";

const AvatarContainer = ({
  url = null,
  shouldShowMentorBanner,
  shouldShowUniversityOfficialBanner,
  dimensions = "w-12 h-12",
  className = "",
  gamePlayerId = "",
  ...props
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgLoadError, setImgLoadError] = useState(false);
  const [showMentorBanner, setShowMentorBanner] = useState(
    shouldShowMentorBanner || false
  );
  const [showUniversityOfficialBanner, setShowUniversityOfficialBanner] =
    useState(shouldShowUniversityOfficialBanner || false);

  /**
   * Callback to fetch provider status when component becomes visible
   * This optimizes performance by deferring API calls until the component is in viewport
   */
  const fetchProviderStatus = useCallback(() => {
    let isMentorPropInvalid = typeof shouldShowMentorBanner === "undefined";
    let isUniversityOfficialPropInvalid =
      typeof shouldShowUniversityOfficialBanner === "undefined";
    let isInvalidProp = isMentorPropInvalid || isUniversityOfficialPropInvalid;
    if (isInvalidProp && gamePlayerId) {
      axios
        .get(
          `${API_URL}/v1/game-player/profile/isApprovedProvider?gamePlayerId=${gamePlayerId}`
        )
        .then(({ data }) => {
          if (data?.isUniversityOfficial) {
            setShowUniversityOfficialBanner(true);
          }
          if (data?.isApprovedGigProvider) {
            setShowMentorBanner(true);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch provider status:", error);
        });
    }
  }, [
    gamePlayerId,
    shouldShowMentorBanner,
    shouldShowUniversityOfficialBanner,
  ]);

  // Only fetch provider status when component is visible in viewport
  const avatarRef = useIntersectionObserver(fetchProviderStatus, {
    threshold: 0.1,
    rootMargin: "50px",
  });

  return (
    <AvatarComponent
      ref={avatarRef}
      imgLoaded={imgLoaded}
      setImgLoaded={setImgLoaded}
      url={Array.isArray(url) ? url[0] : url}
      dimensions={dimensions}
      className={className}
      imgLoadError={imgLoadError}
      setImgLoadError={setImgLoadError}
      showMentorBanner={showMentorBanner}
      showUniversityOfficialBanner={showUniversityOfficialBanner}
      {...props}
    />
  );
};

AvatarContainer.propTypes = {
  /** This field will be the avatar url */
  url: PropTypes.string,

  /** This will set the height and width of your avatar. the values are tailwind height and width classes */
  dimensions: PropTypes.string,

  /** Override or extend the styles applied to the component. */
  className: PropTypes.string,

  shouldShowMentorBanner: PropTypes.bool,
};

export default AvatarContainer;
